/**
 * avatars/presets/textures/other-parts.js
 * Các hàm tạo texture cho: head_side, head_back, arm_bare, blob_shadow, ao_dai_flap, torso_back
 */

import { drawStem, drawLotusLeaf, drawLotusFlower } from './motifs.js';
import { getDefaultShirtColor } from '../shirt-colors.js';
import { CANVAS_FONTS } from '../../../core/fonts.js';

export function generateHeadSideTexture(ctx, size, options = {}) {
  // ----------------------------------------------------
  // MẶT BÊN ĐẦU (TAI & TÓC MAI VOXEL ÔM SÁT TAI - PHẲNG GỌN)
  // ----------------------------------------------------
  const side = options.side || 'right'; // 'right' (+X) hoặc 'left' (-X)
  const skinColor = options.skinColor || '#fcd5b5';
  const hairColor = options.hairColor || '#1e293b';
  const hairStyle = options.hairStyle || 'neat_parted';

  // 1. Nền màu da gốc
  ctx.fillStyle = skinColor;
  ctx.fillRect(0, 0, size, size);

  ctx.save();
  // Đối với mặt bên trái (-X), UV lật ngược so với bên phải nên ta lật gương canvas
  if (side === 'left') {
    ctx.translate(size, 0);
    ctx.scale(-1, 1);
  }

  // Sau khi lật:
  // x = 0 luôn là PHÍA TRƯỚC MẶT (+Z)
  // x = size luôn là PHÍA SAU GÁY (-Z)

  // Tọa độ tai Voxel 2D (làm tròn số nguyên pixel để triệt tiêu hoàn toàn sub-pixel aliasing gap)
  const earX = Math.round(size * 0.3);
  const earY = Math.round(size * 0.44);
  const earW = Math.round(size * 0.16);
  const earH = Math.round(size * 0.22);
  const hairBottomY = Math.round(size * 0.7);

  if (hairStyle !== 'bald') {
    ctx.fillStyle = hairColor;

    // a. Mảng tóc sau tai & gáy: chạy liền mạch 1 khối duy nhất từ đỉnh đầu (y=0) xuống gáy (y=hairBottomY)
    // Hợp nhất toàn bộ từ trên xuống dưới, loại bỏ hoàn toàn đường vạch trắng chia cắt ở earY
    ctx.fillRect(earX + earW - 1, 0, size - (earX + earW) + 2, hairBottomY);

    // b. Mảng tóc mai phía trước: chạy liền mạch 1 khối duy nhất từ đỉnh đầu (y=0) xuống qua tai
    ctx.fillRect(0, 0, earX + 1, earY + Math.round(size * 0.2));

    // c. Khối tóc đỉnh phía trên tai: nối liền giữa tóc mai và tóc sau gáy, phủ chớm qua đỉnh tai 2px
    ctx.fillRect(earX, 0, earW, earY + 2);
  }

  // 2. Tai Voxel 2D (Vành tai + Hốc tai đổ bóng tự nhiên, ôm sát khít vào tóc)
  // Vành tai màu da viền sắc sảo
  ctx.fillStyle = skinColor;
  ctx.fillRect(earX, earY, earW, earH);
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.14)';
  ctx.lineWidth = Math.max(1, Math.round(size * 0.018));
  ctx.strokeRect(earX, earY, earW, earH);

  // Hốc tai đổ bóng có chiều sâu
  ctx.fillStyle = 'rgba(180, 83, 9, 0.30)';
  ctx.fillRect(
    earX + Math.round(earW * 0.25),
    earY + Math.round(earH * 0.25),
    Math.round(earW * 0.5),
    Math.round(earH * 0.5)
  );

  ctx.restore();
}

export function generateHeadBackTexture(ctx, size, options = {}) {
  // ----------------------------------------------------
  // MẶT SAU GÁY (TÓC GÁY THẲNG PHẲNG ĐƠN GIẢN - 0% RĂNG CƯA)
  // ----------------------------------------------------
  const skinColor = options.skinColor || '#fcd5b5';
  const hairColor = options.hairColor || '#1e293b';
  const hairStyle = options.hairStyle || 'neat_parted';

  // 1. Nền màu da cổ và gáy
  ctx.fillStyle = skinColor;
  ctx.fillRect(0, 0, size, size);

  if (hairStyle !== 'bald') {
    ctx.fillStyle = hairColor;

    // a. Phần tóc che phủ gáy: THẲNG NGANG PHẲNG LÌ (y = size * 0.70)
    ctx.fillRect(0, 0, size, size * 0.7);

    // b. Đổ bóng nhẹ chân tóc tiếp giáp cổ
    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
    ctx.fillRect(0, size * 0.68, size, size * 0.02);

    // Nếp highlight tóc sau gáy
    ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.fillRect(size * 0.15, size * 0.22, size * 0.7, size * 0.05);
  }
}
export { generateArmBareTexture } from './arm-bare.js';

export function generateBlobShadowTexture(ctx, size, _options = {}) {
  // ----------------------------------------------------
  // FAKE BLOB SHADOW DƯỚI CHÂN (RADIAL GRADIENT MỀM MẠI)
  // ----------------------------------------------------
  ctx.clearRect(0, 0, size, size);
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.46;

  const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
  gradient.addColorStop(0.0, 'rgba(0, 0, 0, 0.90)');
  gradient.addColorStop(0.45, 'rgba(0, 0, 0, 0.70)');
  gradient.addColorStop(0.8, 'rgba(0, 0, 0, 0.25)');
  gradient.addColorStop(1.0, 'rgba(0, 0, 0, 0.0)');

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fill();
}

export function generateAoDaiFlapTexture(ctx, size, options = {}) {
  // TÀ TRƯỚC ÁO DÀI: nối tiếp cành sen từ thân áo xuống, lá sen & gợn nước ở gấu
  ctx.fillStyle = options.shirtColor || '#ffffff';
  ctx.fillRect(0, 0, size, size);
  const sheen = ctx.createLinearGradient(0, 0, size, 0);
  sheen.addColorStop(0, 'rgba(226, 232, 240, 0.35)');
  sheen.addColorStop(0.45, 'rgba(255, 255, 255, 0)');
  sheen.addColorStop(1, 'rgba(226, 232, 240, 0.35)');
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, size, size);
  drawStem(ctx, [size * 0.8, 0, size * 0.82, size * 0.35, size * 0.7, size * 0.62], size * 0.018);
  drawStem(ctx, [size * 0.84, 0, size * 0.9, size * 0.3, size * 0.88, size * 0.55], size * 0.014);
  drawStem(ctx, [size * 0.72, 0, size * 0.62, size * 0.25, size * 0.4, size * 0.7], size * 0.014);
  drawLotusLeaf(ctx, size * 0.7, size * 0.64, size * 0.2, 0.55);
  drawLotusLeaf(ctx, size * 0.36, size * 0.74, size * 0.15, 0.55);
  drawLotusFlower(ctx, size * 0.86, size * 0.56, size * 0.16, 0.2);
  // Gợn nước xanh nhạt ở gấu tà
  ctx.strokeStyle = 'rgba(125, 211, 252, 0.6)';
  ctx.lineWidth = size * 0.012;
  [0.86, 0.92].forEach((ry, i) => {
    ctx.beginPath();
    for (let x = 0; x <= size; x += size / 16) {
      const y = size * ry + Math.sin((x / size) * Math.PI * 6 + i) * size * 0.012;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  });
}

export function generateTorsoBackTexture(ctx, size, options = {}) {
  // ----------------------------------------------------
  // MẶT SAU LƯNG THÂN MÌNH (TORSO BACK)
  // ----------------------------------------------------
  const outfit = options.outfit || 'default';
  const mainColor = options.shirtColor || getDefaultShirtColor(outfit);

  // Mặc định nền sau lưng là màu vải áo chính
  ctx.fillStyle = mainColor;
  ctx.fillRect(0, 0, size, size);

  // Mặt lưng cao ~1.5 lần bề rộng nên chữ được nén dọc để hiển thị đúng tỉ lệ
  const backText = (text, y, px, color, stroke = null) => {
    ctx.save();
    ctx.translate(size / 2, y);
    ctx.scale(1, 0.67);
    ctx.font = `900 ${px}px ${CANVAS_FONTS.sans}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let fontPx = px;
    while (ctx.measureText(text).width > size * 0.86 && fontPx > 12) {
      fontPx -= 2;
      ctx.font = `900 ${fontPx}px ${CANVAS_FONTS.sans}`;
    }
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = size * 0.025;
      ctx.strokeText(text, 0, 0);
    }
    ctx.fillStyle = color;
    ctx.fillText(text, 0, 0);
    ctx.restore();
  };
  // Cổ áo sau & đường may cầu vai
  const backCollar = (color, h = 0.06) => {
    ctx.fillStyle = color;
    ctx.fillRect(size * 0.3, 0, size * 0.4, size * h);
  };
  const yokeSeam = () => {
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.14)';
    ctx.lineWidth = size * 0.012;
    ctx.beginPath();
    ctx.moveTo(0, size * 0.2);
    ctx.lineTo(size, size * 0.2);
    ctx.stroke();
  };
  const reflectStripe = (y, color = '#e2e8f0') => {
    ctx.fillStyle = color;
    ctx.fillRect(0, size * y, size, size * 0.06);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillRect(0, size * y, size, size * 0.015);
  };

  if (outfit === 'grab') {
    backCollar('#008a3d');
    backText('Grab', size * 0.4, 96, '#ffffff');
    reflectStripe(0.68);
  } else if (outfit === 'be') {
    backCollar('#18181b');
    backText('be', size * 0.4, 120, '#18181b');
    ctx.fillStyle = '#18181b';
    ctx.fillRect(0, size * 0.68, size, size * 0.07);
  } else if (outfit === 'xanh_sm') {
    backCollar('#0e7490');
    backText('XANH SM', size * 0.4, 70, '#ffffff');
    reflectStripe(0.68);
  } else if (outfit === 'shopee') {
    backCollar('#c2410c');
    backText('EXPRESS', size * 0.36, 64, '#ffffff');
    reflectStripe(0.62);
    reflectStripe(0.78);
  } else if (outfit === 'bao_ve') {
    backCollar('#0369a1');
    yokeSeam();
    backText('BẢO VỆ', size * 0.45, 74, '#facc15', '#0c4a6e');
  } else if (outfit === 'hoc_sinh_vn') {
    // Vạt tam giác khăn quàng đỏ sau gáy
    backCollar('#e2e8f0', 0.05);
    ctx.fillStyle = '#dc2626';
    ctx.beginPath();
    ctx.moveTo(size * 0.26, 0);
    ctx.lineTo(size * 0.74, 0);
    ctx.lineTo(size * 0.5, size * 0.3);
    ctx.closePath();
    ctx.fill();
    yokeSeam();
  } else if (outfit === 'suit') {
    backCollar('#0f172a', 0.07);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.lineWidth = size * 0.012;
    ctx.beginPath();
    ctx.moveTo(size * 0.5, size * 0.07);
    ctx.lineTo(size * 0.5, size);
    ctx.stroke();
  } else if (outfit === 'lab_coat' || options.isLabCoat) {
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, size, size);
    backCollar('#e2e8f0', 0.08);
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = size * 0.014;
    ctx.beginPath();
    ctx.moveTo(size * 0.5, size * 0.55);
    ctx.lineTo(size * 0.5, size);
    ctx.moveTo(size * 0.15, size * 0.62);
    ctx.lineTo(size * 0.85, size * 0.62);
    ctx.stroke();
  } else if (outfit === 'so_mi' || outfit === 'so_mi_can_bo' || outfit === 'default') {
    backCollar(outfit === 'default' ? 'rgba(0,0,0,0.15)' : '#e2e8f0');
    yokeSeam();
  } else if (outfit === 'ao_dai') {
    // Cổ đứng & đường may sống lưng
    backCollar('#f1f5f9', 0.1);
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.5)';
    ctx.lineWidth = size * 0.01;
    ctx.beginPath();
    ctx.moveTo(size * 0.5, size * 0.1);
    ctx.lineTo(size * 0.5, size);
    ctx.stroke();
  } else if (outfit === 'ao_ba_ba') {
    ctx.fillStyle = options.skinColor || '#fcd5b5';
    ctx.beginPath();
    ctx.ellipse(size * 0.5, 0, size * 0.16, size * 0.05, 0, 0, Math.PI);
    ctx.fill();
  } else if (outfit === 'genz_oversize') {
    ctx.fillStyle = '#38bdf8';
    ctx.fillRect(size * 0.4, size * 0.3, size * 0.2, size * 0.03);
    backText('HEALING', size * 0.5, 60, '#f43f5e');
    ctx.fillStyle = '#10b981';
    ctx.fillRect(size * 0.3, size * 0.6, size * 0.4, size * 0.03);
  } else if (outfit === 'tien_loi') {
    // Dây tạp dề bắt chéo sau lưng & nơ thắt eo
    ctx.strokeStyle = '#18181b';
    ctx.lineWidth = size * 0.06;
    ctx.beginPath();
    ctx.moveTo(size * 0.2, 0);
    ctx.lineTo(size * 0.8, size * 0.6);
    ctx.moveTo(size * 0.8, 0);
    ctx.lineTo(size * 0.2, size * 0.6);
    ctx.stroke();
    ctx.fillStyle = '#18181b';
    ctx.fillRect(0, size * 0.6, size, size * 0.07);
    ctx.fillRect(size * 0.42, size * 0.58, size * 0.16, size * 0.12);
    ctx.fillRect(size * 0.44, size * 0.68, size * 0.05, size * 0.18);
    ctx.fillRect(size * 0.52, size * 0.68, size * 0.05, size * 0.16);
  } else if (outfit === 'ao_ba_lo') {
    // ÁO BA LỖ THỂ THAO / DÂN DÃ - MẶT SAU LƯNG
    // Khoét nách sau cong vào bả vai và khoét cổ sau cạn hơn cổ trước
    const skin = options.skinColor || '#fcd5b5';

    // 1. Khoét nách sau hai bên bả vai
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(size * 0.18, 0);
    ctx.quadraticCurveTo(size * 0.12, size * 0.32, 0, size * 0.44);
    ctx.closePath();
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(size, 0);
    ctx.lineTo(size * 0.82, 0);
    ctx.quadraticCurveTo(size * 0.88, size * 0.32, size, size * 0.44);
    ctx.closePath();
    ctx.fill();

    // Viền may bo nách sau
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.18)';
    ctx.lineWidth = size * 0.016;
    ctx.beginPath();
    ctx.moveTo(size * 0.18, 0);
    ctx.quadraticCurveTo(size * 0.12, size * 0.32, 0, size * 0.44);
    ctx.moveTo(size * 0.82, 0);
    ctx.quadraticCurveTo(size * 0.88, size * 0.32, size, size * 0.44);
    ctx.stroke();

    // 2. Cổ sau khoét cạn
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.ellipse(size * 0.5, 0, size * 0.2, size * 0.07, 0, 0, Math.PI);
    ctx.fill();

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.lineWidth = size * 0.02;
    ctx.beginPath();
    ctx.ellipse(size * 0.5, 0, size * 0.2, size * 0.07, 0, 0, Math.PI);
    ctx.stroke();
  }
}
